import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/apiService";

/**
 * Hook personalizado para búsqueda global en el sistema
 * Busca en plantas, procesos, salas, máquinas, catálogos y usuarios
 */
const useGlobalSearch = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  /**
   * Extrae array de datos de la respuesta de la API
   */
  const extractDataArray = (apiResponse, entityName) => {
    if (!apiResponse) return [];

    // Si ya es un array
    if (Array.isArray(apiResponse)) return apiResponse;

    // Si tiene estructura { data: { plants: [...] } }
    if (apiResponse.data && apiResponse.data[entityName]) {
      return Array.isArray(apiResponse.data[entityName]) ? apiResponse.data[entityName] : [];
    }

    // Si tiene propiedad data directa como array
    if (apiResponse.data && Array.isArray(apiResponse.data)) {
      return apiResponse.data;
    }

    // Si tiene propiedad results
    if (apiResponse.results) {
      return Array.isArray(apiResponse.results) ? apiResponse.results : [];
    }

    // Si tiene propiedad items
    if (apiResponse.items) {
      return Array.isArray(apiResponse.items) ? apiResponse.items : [];
    }

    return [];
  };

  /**
   * Realiza la búsqueda en todas las entidades del sistema
   */
  const performSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    setShowResults(true);
    const searchResults = [];

    try {
      // Usar los endpoints de búsqueda específicos con parámetro 'q'
      const searchParam = `?q=${encodeURIComponent(term.trim())}`;

      const [plants, processes, rooms, machines, catalogs, users] = await Promise.allSettled([
        apiService.get(`/plants/search${searchParam}`),
        apiService.get(`/processes/search${searchParam}`),
        apiService.get(`/rooms/search${searchParam}`),
        apiService.get(`/machines/search${searchParam}`),
        apiService.get(`/catalogs/search${searchParam}`),
        apiService.get(`/users/search${searchParam}`),
      ]);

      // Log de errores individuales
      if (plants.status === "rejected") console.error("❌ Error en /plants/search:", plants.reason);
      if (processes.status === "rejected") console.error("❌ Error en /processes/search:", processes.reason);
      if (rooms.status === "rejected") console.error("❌ Error en /rooms/search:", rooms.reason);
      if (machines.status === "rejected") console.error("❌ Error en /machines/search:", machines.reason);
      if (catalogs.status === "rejected") console.error("❌ Error en /catalogs/search:", catalogs.reason);
      if (users.status === "rejected") console.error("❌ Error en /users/search:", users.reason);

      // Ya no necesitamos filtrar en el frontend, el backend ya lo hace
      // const searchLower = term.toLowerCase();

      // Procesar plantas
      if (plants.status === "fulfilled" && plants.value) {
        const data = extractDataArray(plants.value, "plants");
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((item) => {
            searchResults.push({
              id: item._id,
              type: "plant",
              title: item.name,
              subtitle: item.code ? `Código: ${item.code}` : item.location?.city || "Planta",
              path: `/plants/${item._id}`,
              icon: "Business",
            });
          });
        }
      }

      // Procesar procesos
      if (processes.status === "fulfilled" && processes.value) {
        const data = extractDataArray(processes.value, "processes");
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((item) => {
            searchResults.push({
              id: item._id,
              type: "process",
              title: item.name,
              subtitle: item.code ? `Código: ${item.code}` : "Proceso",
              path: `/processes/${item._id}`,
              icon: "Settings",
            });
          });
        }
      }

      // Procesar salas
      if (rooms.status === "fulfilled" && rooms.value) {
        const data = extractDataArray(rooms.value, "rooms");
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((item) => {
            searchResults.push({
              id: item._id,
              type: "room",
              title: item.name,
              subtitle: item.code ? `Código: ${item.code}` : "Sala",
              path: `/rooms/${item._id}`,
              icon: "Room",
            });
          });
        }
      }

      // Procesar máquinas
      if (machines.status === "fulfilled" && machines.value) {
        const data = extractDataArray(machines.value, "machines");
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((item) => {
            searchResults.push({
              id: item._id,
              type: "machine",
              title: item.name,
              subtitle: item.serialNumber ? `S/N: ${item.serialNumber}` : item.code || "Máquina",
              path: `/machines/${item._id}`,
              icon: "PrecisionManufacturing",
            });
          });
        }
      }

      // Procesar catálogos
      if (catalogs.status === "fulfilled" && catalogs.value) {
        const data = extractDataArray(catalogs.value, "catalogs");
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((item) => {
            searchResults.push({
              id: item._id,
              type: "catalog",
              title: item.name,
              subtitle: item.version ? `v${item.version}` : "Catálogo",
              path: `/catalogs`,
              icon: "Description",
            });
          });
        }
      }

      // Procesar usuarios
      if (users.status === "fulfilled" && users.value) {
        const data = extractDataArray(users.value, "users");
        if (Array.isArray(data) && data.length > 0) {
          data.forEach((item) => {
            searchResults.push({
              id: item._id,
              type: "user",
              title: item.name,
              subtitle: item.email,
              path: `/users`,
              icon: "Person",
            });
          });
        }
      }

      setResults(searchResults.slice(0, 10)); // Limitar a 10 resultados
    } catch (error) {
      console.error("❌ Error en búsqueda global:", error);
      console.error("Stack:", error.stack);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Debounce para la búsqueda
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchTerm);
    }, 300); // Esperar 300ms después de que el usuario deje de escribir

    return () => clearTimeout(timer);
  }, [searchTerm, performSearch]);

  /**
   * Navegar a un resultado
   */
  const navigateToResult = useCallback(
    (result) => {
      navigate(result.path);
      setSearchTerm("");
      setResults([]);
      setShowResults(false);
    },
    [navigate]
  );

  /**
   * Limpiar búsqueda
   */
  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setResults([]);
    setShowResults(false);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    showResults,
    setShowResults,
    navigateToResult,
    clearSearch,
  };
};

export default useGlobalSearch;
